import React, { useState, useRef, useEffect } from 'react';

const Toolbar = ({
  currentPath,
  expedienteFolderName,
  canGoBack,
  canGoForward,
  canGoUp,
  selectedCount,
  viewMode,
  searchTerm,
  clipboard,
  onGoBack,
  onGoForward,
  onGoUp,
  onNavigateTo,
  onToggleView,
  onSearch,
  onDelete,
  onCopy,
  onCut,
  onPaste,
  onRefresh,
  onUpload
}) => {
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMoreMenu]);

  const pathParts = currentPath ? currentPath.split('/') : [];
  const currentFolderName = pathParts.length > 0 ? pathParts[pathParts.length - 1] : 'Raíz';
  
  return (
    <div className="toolbar toolbar-compact">
      {/* Navegación básica */}
      <div className="toolbar-group">
        <button
          className="toolbar-btn toolbar-btn-nav"
          onClick={onGoBack}
          disabled={!canGoBack}
          title="Atrás"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button
          className="toolbar-btn toolbar-btn-nav"
          onClick={onGoForward}
          disabled={!canGoForward}
          title="Adelante"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
        <button
          className="toolbar-btn toolbar-btn-nav"
          onClick={onGoUp}
          disabled={!canGoUp}
          title="Subir nivel"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="18 15 12 9 6 15"></polyline>
          </svg>
        </button>
      </div>

      <div className="toolbar-separator"></div>

      {/* Ruta actual simplificada */}
      <div className="toolbar-current-path">
        <span className="path-icon">📁</span>
        <span className="path-text">{currentFolderName}</span>
      </div>

      <div className="toolbar-spacer"></div>

      {/* Acciones principales */}
      <div className="toolbar-group">
        {onUpload && (
          <button
            className="toolbar-btn toolbar-btn-success toolbar-btn-upload"
            onClick={onUpload}
            title="Subir archivos"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            <span className="toolbar-btn-upload-label">Subir</span>
          </button>
        )}

        {selectedCount > 0 && (
          <>
            <button
              className="toolbar-btn"
              onClick={onCopy}
              title="Copiar (Ctrl+C)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
            <button
              className="toolbar-btn toolbar-btn-danger"
              onClick={onDelete}
              title="Eliminar (Delete)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </>
        )}

        {/* Búsqueda compacta */}
        <div className="toolbar-search toolbar-search-compact">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="search-clear"
              onClick={() => onSearch('')}
              title="Limpiar"
            >
              ×
            </button>
          )}
        </div>

        {/* Botón de más opciones */}
        <div className="toolbar-more-menu" ref={moreMenuRef}>
          <button
            className="toolbar-btn"
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            title="Más opciones"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="12" cy="5" r="1"></circle>
              <circle cx="12" cy="19" r="1"></circle>
            </svg>
          </button>

          {showMoreMenu && (
            <div className="more-menu-dropdown">
              {onUpload && (
                <button onClick={() => { onUpload(); setShowMoreMenu(false); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  Subir archivos
                </button>
              )}

              {selectedCount > 0 && (
                <>
                  <button onClick={() => { onCut(); setShowMoreMenu(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="6" cy="6" r="3"></circle>
                      <circle cx="6" cy="18" r="3"></circle>
                      <line x1="20" y1="4" x2="8.12" y2="15.88"></line>
                      <line x1="14.47" y1="14.48" x2="20" y2="20"></line>
                      <line x1="8.12" y1="8.12" x2="12" y2="12"></line>
                    </svg>
                    Cortar
                  </button>
                </>
              )}

              {clipboard.items?.length > 0 && (
                <button onClick={() => { onPaste(); setShowMoreMenu(false); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                  </svg>
                  Pegar ({clipboard.items.length})
                </button>
              )}

              <button onClick={() => { onToggleView(); setShowMoreMenu(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
                Vista {viewMode === 'details' ? 'Cuadrícula' : 'Detalles'}
              </button>

              <button onClick={() => { onRefresh(); setShowMoreMenu(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
                Actualizar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Indicadores */}
      {selectedCount > 0 && (
        <span className="toolbar-badge toolbar-badge-selection">
          {selectedCount}
        </span>
      )}

      {clipboard.items?.length > 0 && (
        <span className="toolbar-badge toolbar-badge-clipboard" title={`${clipboard.items.length} elemento(s) ${clipboard.operation === 'cut' ? 'cortado(s)' : 'copiado(s)'}`}>
          {clipboard.operation === 'cut' ? '✂️' : '📋'} {clipboard.items.length}
        </span>
      )}
    </div>
  );
};

export default Toolbar;
